/**
 * RoleSelector - the "Assign Roles" picker, shared by every create/edit-user surface.
 *
 * Atomic Design role: Molecule. Renders the roles the current user is allowed
 * to assign (GET /users/available-roles) as a multi-select checkbox group, each
 * with the one-line description of what the role can actually do. The caller
 * owns the selected array and the fetch.
 *
 * This exists because the two places that create a user had drifted apart: the
 * Admin page offered a multi-select checkbox group with descriptions, while the
 * global header's Add User modal offered a single-select <select> with bare
 * slugs. Same endpoint, same payload, two different answers to "what can this
 * person do" — and the modal couldn't express the multi-role assignments the
 * user_roles junction table is built around. One component now answers it once.
 *
 * Layout note: the rows are `flex-shrink-0` on purpose. The group scrolls at
 * `maxHeight`, and a constrained flex column will happily compress its items
 * below their content height — which is what made the labels sit on top of the
 * previous row's description instead of below it.
 *
 * @example
 * <RoleSelector options={availableRoles} value={form.roles} onChange={(roles) => setField('roles', roles)} />
 */
import React from 'react';
import { Checkbox } from '../../atoms/Checkbox';
import { formatRoleLabel, formatRoleDescription } from '../../../utils/formatLabel';

export interface RoleSelectorProps {
	/** Role slugs the current user may assign, in display order. */
	options: string[];
	/** Currently selected role slugs. */
	value: string[];
	/** Called with the next selection whenever a box is ticked or cleared. */
	onChange: (roles: string[]) => void;
	/** Group heading. Defaults to "Assign Roles". */
	label?: string;
	/** Adds the required marker to the heading. */
	required?: boolean;
	/** Shows a loading line instead of the options. */
	loading?: boolean;
	/** Inline validation message shown under the group. */
	error?: string;
	/** Copy shown when there is nothing to assign. */
	emptyText?: string;
	/** Max height of the scroll area. Defaults to 280px. */
	maxHeight?: number;
	/** Extra classes merged onto the root. */
	className?: string;
	/** Overrides the molecule's data-ref so a composing component keeps its identity. */
	dataRef?: string;
	/** Overrides the per-row data-ref. */
	optionDataRef?: string;
}

export const RoleSelector: React.FC<RoleSelectorProps> = ({
	options,
	value,
	onChange,
	label = 'Assign Roles',
	required = false,
	loading = false,
	error,
	emptyText = 'No roles available to assign.',
	maxHeight = 280,
	className = '',
	dataRef = 'molecule-role-selector',
	optionDataRef = 'molecule-role-selector-option',
}) => {
	/** Tick / untick one role, preserving the order the options came in so the
	 *  payload doesn't reshuffle every time a box is toggled. */
	const toggle = (roleName: string, checked: boolean) => {
		onChange(
			checked
				? options.filter((role) => role === roleName || value.includes(role))
				: value.filter((role) => role !== roleName),
		);
	};

	return (
		<div
			className={['role-selector', 'border border-brand-border rounded-md p-12px', className].filter(Boolean).join(' ')}
			data-ref={dataRef}
		>
			<p className="role-selector__label text-12px font-medium text-brand-text-muted uppercase tracking-wide mb-8px">
				{label}
				{required && <span className="text-red-500 ml-2px">*</span>}
			</p>

			{loading && <p className="text-12px text-brand-text-subtle italic">Loading roles…</p>}
			{!loading && options.length === 0 && <p className="text-12px text-brand-text-subtle italic">{emptyText}</p>}

			{/* One role per line. The rows have to be flex ITEMS to stack: Checkbox's
			    root is an inline-flex <label>, so in a plain block container they flow
			    inline and wrap into a paragraph of checkboxes. flex-shrink-0 then stops
			    the capped column from squeezing a two-line row into one row's height. */}
			{!loading && options.length > 0 && (
				<div className="role-selector__options flex flex-col gap-12px overflow-y-auto" style={{ maxHeight: `${maxHeight}px` }}>
					{options.map((roleName) => (
						/* No items-center here: with a description under the label the box
						   belongs beside the FIRST line, which is the atom's own default. */
						<Checkbox
							key={roleName}
							checked={value.includes(roleName)}
							onChange={(checked) => toggle(roleName, checked)}
							label={formatRoleLabel(roleName)}
							description={formatRoleDescription(roleName)}
							className="role-selector__option flex-shrink-0"
							dataRef={optionDataRef}
						/>
					))}
				</div>
			)}

			{error && <p className="role-selector__error mt-8px text-14px font-sans text-red-600">{error}</p>}
		</div>
	);
};

export default RoleSelector;
